import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Archive, CalendarClock, CheckCircle2, Loader2, Lock, LockOpen, Pencil, Plus, RadioTower, Search, Target, Trash2, Trophy, WifiOff } from "lucide-react";
import { AdminTopbar } from "../../layouts/AdminLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { BulkBar, RowBox, SelectAllBox, useTableSelection } from "../../components/TableSelection";
import { Badge, Button, EventTypeChip, Modal, Pagination, Select, StatCard, statusTone } from "../../components/ui";
import { adminSession, apiDelete, apiPost, apiPut, useApi } from "../../api/client";
import { Competition } from "../../data/types";

const emptyForm: Partial<Competition> = {
  name: "",
  type: "Open",
  discipline: "Precision Rifle",
  startDate: "",
  endDate: "",
  days: 1,
  range: "",
  city: "",
  stateCode: "QLD",
  country: "Australia",
  status: "UPCOMING",
  organiser: "",
  sanctionedBy: "SSAA",
  matchDirector: "",
  contactEmail: "",
  description: "",
  liveScoring: false,
};


export default function AdminCompetitions() {
  const navigate = useNavigate();
  const { data: all, reload } = useApi<Competition[]>("/competitions", []);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [stateFilter, setStateFilter] = useState("All States");
  const [levelFilter, setLevelFilter] = useState("All Levels");
  const [countryFilter, setCountryFilter] = useState("All Countries");
  const [typeFilter, setTypeFilter] = useState("All Types");
  // A meeting is remembered by when it was shot as much as by its name, so the
  // search row carries a date window alongside the other fields.
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Options come from the data, so a value the Event sheet uses can be filtered for.
  const stateOptions = useMemo(
    () => Array.from(new Set(all.map((c) => c.stateCode).filter(Boolean))).sort(),
    [all]
  );
  const countryOptions = useMemo(
    () => Array.from(new Set(all.map((c) => c.country).filter(Boolean))).sort() as string[],
    [all]
  );
  const levelOptions = useMemo(
    () => Array.from(new Set(all.map((c) => c.eventLevel).filter(Boolean))).sort() as string[],
    [all]
  );
  const typeOptions = useMemo(
    () => Array.from(new Set(all.map((c) => c.eventStatus || c.type).filter(Boolean))).sort() as string[],
    [all]
  );

  const q = search.trim().toLowerCase();
  const competitions = all.filter(
    (c) =>
      (!q || `${c.name} ${c.eid ?? ""} ${c.range ?? ""} ${c.city ?? ""}`.toLowerCase().includes(q)) &&
      (statusFilter === "All Status" || c.status === statusFilter.toUpperCase()) &&
      (stateFilter === "All States" || c.stateCode === stateFilter) &&
      (countryFilter === "All Countries" || c.country === countryFilter) &&
      (levelFilter === "All Levels" || c.eventLevel === levelFilter) &&
      (typeFilter === "All Types" || (c.eventStatus || c.type) === typeFilter) &&
      /*
       * An event matches the window if any part of it falls inside — a two-day
       * shoot found by searching either of its days, not only its first.
       */
      (!fromDate || (c.endDateISO ?? c.startDateISO ?? "") >= fromDate) &&
      (!toDate || (c.startDateISO ?? c.endDateISO ?? "") <= toDate)
  );
  const selection = useTableSelection(competitions.map((c) => c.id));
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Competition>>(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k: keyof Competition, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError("");
    setOpen(true);
  };
  const openEdit = (c: Competition) => {
    setForm(c);
    setEditingId(c.id);
    setError("");
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) {
      setError("Event name is required.");
      return;
    }
    if (!adminSession.token()) {
      setError("Sign in first (admin / admin123) — changes are authenticated.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingId) await apiPut(`/admin/competitions/${editingId}`, form);
      else await apiPost("/admin/competitions", form);
      setOpen(false);
      reload();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  /*
   * One code path for one row or many — a single delete is a pending list of
   * one — so the confirmation, progress and error handling are identical
   * however it was started.
   */
  const [pending, setPending] = useState<Competition[]>([]);
  const [removing, setRemoving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const removeSelected = async () => {
    setRemoving(true);
    const failed: string[] = [];
    for (const c of pending) {
      try {
        await apiDelete(`/admin/competitions/${c.id}`);
      } catch (e: any) {
        failed.push(`${c.name}: ${e.message}`);
      }
    }
    setRemoving(false);
    setPending([]);
    selection.clear();
    reload();
    setNotice(
      failed.length
        ? `${pending.length - failed.length} of ${pending.length} removed. ${failed[0]}`
        : `${pending.length} competition${pending.length === 1 ? "" : "s"} removed, with all of their results.`
    );
  };

  return (
    <div>
      <AdminTopbar
        title="Events"
        subtitle="Every shoot the portal holds — each one identified by its EID."
        actions={<Button onClick={openCreate}><Plus size={16} /> Create Event</Button>}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={<Trophy size={20} />} value={competitions.length} label="Total Events" />
        <StatCard icon={<CalendarClock size={20} />} value={competitions.filter((c) => c.status === "UPCOMING").length} label="Upcoming" iconBg="bg-success-soft text-success" />
        <StatCard icon={<RadioTower size={20} />} value={competitions.filter((c) => c.status === "LIVE").length} label="Live" sub="In Progress" iconBg="bg-warning-soft text-warning" />
        <StatCard icon={<CheckCircle2 size={20} />} value={competitions.filter((c) => c.status === "COMPLETED").length} label="Completed" iconBg="bg-violet-50 text-violet-600" />
        <StatCard icon={<Archive size={20} />} value={competitions.filter((c) => c.status === "ARCHIVED").length} label="Archived" />
      </div>

      {/*
        Working filters. These were decorative — four dropdowns with no state
        behind them — so a list of a hundred events could only be read by
        scrolling. State and level come from the Event sheet.
      */}
      <div className="card mt-3 flex flex-wrap items-end gap-3 p-3">
        <div className="w-72 flex-1">
          <label className="label">Search</label>
          <div className="relative">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Event name, EID or range..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="label">Status</label>
          <Select
            options={["All Status", "Live", "Completed", "Upcoming", "Archived"]}
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-full sm:w-36"
          />
        </div>
        <div>
          <label className="label">State</label>
          <Select
            options={["All States", ...stateOptions]}
            value={stateFilter}
            onChange={setStateFilter}
            className="w-full sm:w-32"
          />
        </div>
        <div>
          <label className="label">Country</label>
          <Select
            options={["All Countries", ...countryOptions]}
            value={countryFilter}
            onChange={setCountryFilter}
            className="w-full sm:w-40"
          />
        </div>
        <div>
          <label className="label">Event Level</label>
          <Select
            options={["All Levels", ...levelOptions]}
            value={levelFilter}
            onChange={setLevelFilter}
            className="w-full sm:w-40"
          />
        </div>
        <div>
          <label className="label">Event Type</label>
          <Select
            options={["All Types", ...typeOptions]}
            value={typeFilter}
            onChange={setTypeFilter}
            className="w-full sm:w-36"
          />
        </div>
        <div>
          <label className="label">Shot From</label>
          <input type="date" className="input w-full sm:w-40" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Shot To</label>
          <input type="date" className="input w-full sm:w-40" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setSearch("");
            setStatusFilter("All Status");
            setStateFilter("All States");
            setLevelFilter("All Levels");
            setCountryFilter("All Countries");
            setTypeFilter("All Types");
            setFromDate("");
            setToDate("");
          }}
        >
          Reset
        </Button>
      </div>

      {notice && (
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-success/30 bg-success-soft px-4 py-2.5 text-[13px] font-semibold text-success">
          <CheckCircle2 size={16} /> {notice}
          <button className="ml-auto font-bold opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      <BulkBar
        count={selection.count}
        noun="competition"
        onClear={selection.clear}
        onDelete={() => setPending(competitions.filter((c) => selection.isSelected(c.id)))}
      />

      <div className="card mt-4">
        <div className="table-scroll [--table-offset:370px]">
          <table className="w-full min-w-[1020px]">
          <thead>
            <tr className="border-b border-line bg-slate-50">
              <th className="th w-10">
                <SelectAllBox
                  checked={selection.allSelected}
                  indeterminate={selection.someSelected}
                  onChange={selection.toggleAll}
                  label="Select all competitions"
                />
              </th>
              <th className="th">Event</th>
              <th className="th">Type</th>
              <th className="th">Level</th>
              <th className="th">Date Range</th>
              <th className="th text-center">Competitions</th>
              <th className="th text-center">Matches</th>
              <th className="th text-center">Shooters</th>
              <th className="th text-center">Shots</th>
              <th className="th">Status</th>
              <th className="th" title="The date after which only the Association can amend these results — counted from the last upload, and set on the Settings page.">
                Results Lock
              </th>
              <th className="th" title="Whether the portal treats this event as running, refreshing results as uploads arrive. On automatically while today falls inside the event's dates.">
                Live Scoring
              </th>
              <th className="th text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {!competitions.length && (
              <tr>
                <td colSpan={13} className="td py-10 text-center text-muted">
                  No competitions yet — import a shoot workbook to populate the portal.
                </td>
              </tr>
            )}
            {competitions.map((c) => (
              <tr
                key={c.id}
                // Row click drills into this competition's matches (pre-filtered combobox)
                onClick={() => navigate(`/admin/matches?competitionId=${c.id}`)}
                className={`cursor-pointer transition-colors ${selection.isSelected(c.id) ? "bg-primary-soft/50" : "hover:bg-slate-50"}`}
              >
                <td className="td" onClick={(e) => e.stopPropagation()}>
                  <RowBox checked={selection.isSelected(c.id)} onChange={() => selection.toggle(c.id)} label={`Select ${c.name}`} />
                </td>
                <td className="td">
                  <div className="flex items-center gap-3.5">
                    <Target size={26} className="shrink-0 text-accent" strokeWidth={1.75} />
                    <div>
                      <div className="font-bold">{c.name}</div>
                      <div className="text-[13px] text-muted">
                        {[c.range, c.stateCode, c.country].filter(Boolean).join(", ") || c.discipline}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="td"><EventTypeChip type={c.eventStatus || c.type} /></td>
                <td className="td text-slate-600">{c.eventLevel || "—"}</td>
                <td className="td">
                  <div className="font-semibold">{c.startDate}{c.endDate && c.endDate !== c.startDate ? ` – ${c.endDate}` : ""}</div>
                  <div className="text-[13px] text-muted">{c.days} Days</div>
                </td>
                {/* The CIDs shot within this event — Bronger, Carrington — not
                    the days it spans, which are shown under the date range. */}
                <td className="td text-center font-semibold">{c.competitions ?? 1}</td>
                <td className="td text-center font-semibold">{c.matches}</td>
                <td className="td text-center font-semibold">{c.shooters ?? 0}</td>
                <td className="td text-center font-semibold">{(c.shotsRecorded ?? 0).toLocaleString()}</td>
                <td className="td"><Badge tone={statusTone(c.status)}>{c.status}</Badge></td>
                <td className="td">
                  {/* When a club can no longer amend its own results. */}
                  {c.lock ? (
                    c.lock.locked ? (
                      <span className="flex items-center gap-1.5 text-[13px] font-bold text-muted">
                        <Lock size={14} /> Locked
                      </span>
                    ) : (
                      <>
                        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
                          <LockOpen size={14} className="text-success" /> {c.lock.locksAt}
                        </span>
                        <div className="mt-0.5 text-[11px] text-muted">
                          {c.lock.daysLeft} day{c.lock.daysLeft === 1 ? "" : "s"} left
                        </div>
                      </>
                    )
                  ) : (
                    <span className="text-[13px] text-muted">—</span>
                  )}
                </td>
                <td className="td">
                  {c.liveScoring ? (
                    <span className="flex items-center gap-1.5 text-[13px] font-bold text-success"><RadioTower size={15} /> Enabled</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[13px] font-bold text-muted"><WifiOff size={15} /> Disabled</span>
                  )}
                  {/* Says whether this was decided by the dates or set by hand. */}
                  <div className="mt-0.5 text-[11px] text-muted">{c.liveScoringAuto === false ? "Set manually" : "Automatic"}</div>
                </td>
                <td className="td text-right" onClick={(e) => e.stopPropagation()}>
                  <span className="inline-flex items-center gap-1.5">
                    <button
                      onClick={() => openEdit(c)}
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line px-3 text-[13px] font-semibold transition-colors hover:bg-slate-50"
                    >
                      <Pencil size={13} /> Edit
                    </button>
                    <button
                      onClick={() => setPending([c])}
                      className="rounded-lg border border-line p-1.5 text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                      aria-label="Delete competition"
                    >
                      <Trash2 size={15} />
                    </button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="border-t border-line">
          <Pagination summary={`Showing 1 to ${competitions.length} of ${competitions.length} competitions`} pages={1} />
        </div>
      </div>

      <ConfirmDialog
        open={pending.length > 0}
        busy={removing}
        title={pending.length === 1 ? "Remove this event?" : `Remove ${pending.length} events?`}
        intro="This removes the competition and everything recorded under it."
        items={pending.map((c) => `${c.name} — ${c.events ?? 0} events, ${c.matches ?? 0} matches`)}
        note={
          <>
            Every event, match, shot string and result belonging to {pending.length === 1 ? "it" : "them"} is removed, and
            shooter statistics and rankings are recalculated from what remains. Competitors stay on the register.{" "}
            <b>This cannot be undone</b> — the data returns only by importing the workbook again.
          </>
        }
        confirmLabel={pending.length === 1 ? "Remove competition" : `Remove ${pending.length} competitions`}
        onConfirm={removeSelected}
        onCancel={() => setPending([])}
      />

      <Modal
        title={editingId ? "Edit Event" : "Create Event"}
        open={open}
        onClose={() => setOpen(false)}
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy}>
              {busy && <Loader2 size={15} className="animate-spin" />}
              {editingId ? "Save Changes" : "Create Event"}
            </Button>
          </>
        }
      >
        {error && (
          <div className="mb-4 flex items-center gap-2.5 rounded-lg bg-danger-soft px-4 py-3 text-sm font-semibold text-danger">
            <AlertCircle size={16} /> {error}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">Event Name <span className="text-danger">*</span></label>
            <input className="input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="e.g. 2024 State Championship" />
          </div>
          <div>
            <label className="label">Type</label>
            <Select options={["Open", "National", "State", "Series", "Regional"]} value={form.type} onChange={(v) => set("type", v)} />
          </div>
          <div>
            <label className="label">Discipline</label>
            <input className="input" value={form.discipline || ""} onChange={(e) => set("discipline", e.target.value)} />
          </div>
          <div>
            <label className="label">Start Date</label>
            <input className="input" value={form.startDate || ""} onChange={(e) => set("startDate", e.target.value)} placeholder="May 21, 2024" />
          </div>
          <div>
            <label className="label">End Date</label>
            <input className="input" value={form.endDate || ""} onChange={(e) => set("endDate", e.target.value)} placeholder="May 30, 2024" />
          </div>
          <div>
            <label className="label">Range</label>
            <input className="input" value={form.range || ""} onChange={(e) => set("range", e.target.value)} />
          </div>
          <div>
            <label className="label">City</label>
            <input className="input" value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
          </div>
          <div>
            <label className="label">State</label>
            <Select options={["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"]} value={form.stateCode} onChange={(v) => set("stateCode", v)} />
          </div>
          <div>
            <label className="label">Status</label>
            <Select options={["UPCOMING", "LIVE", "COMPLETED", "ARCHIVED"]} value={form.status} onChange={(v) => set("status", v)} />
          </div>
          <div>
            <label className="label">Match Director</label>
            <input className="input" value={form.matchDirector || ""} onChange={(e) => set("matchDirector", e.target.value)} />
          </div>
          <div>
            <label className="label">Organiser</label>
            <input className="input" value={form.organiser || ""} onChange={(e) => set("organiser", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Description</label>
            <textarea className="input h-20 resize-none py-2.5" value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
          </div>
          {/*
            Status follows the shoot dates, which is right almost always and
            corrects itself as the meeting passes. A shoot abandoned for weather
            or held back from publication is the exception, so it can be pinned —
            with "automatic" as the third and normal answer.
          */}
          <div className="sm:col-span-2">
            <label className="label">Status</label>
            <Select
              options={["Automatic — follows the shoot dates", "Upcoming", "Live", "Completed", "Archived"]}
              value={
                form.statusOverride
                  ? String(form.statusOverride).charAt(0) + String(form.statusOverride).slice(1).toLowerCase()
                  : "Automatic — follows the shoot dates"
              }
              onChange={(v) => set("statusMode", v.startsWith("Automatic") ? "auto" : v.toLowerCase())}
              className="w-full sm:w-72"
            />
            <p className="mt-1 text-xs text-muted">
              On automatic a shoot is Upcoming before it starts, Live on its days, and Completed after the last day.
            </p>
          </div>
          {/*
            Live scoring follows the shoot dates by default, so it switches off
            by itself once a meeting is over. A toggle could only say on or off,
            which is what left finished shoots advertising themselves as live —
            so this offers the third and normal answer: let the dates decide.
          */}
          <div className="sm:col-span-2">
            <label className="label">Live scoring</label>
            <Select
              options={["Automatic — follows the shoot dates", "Always on", "Always off"]}
              value={
                form.liveScoringOverride === true
                  ? "Always on"
                  : form.liveScoringOverride === false
                    ? "Always off"
                    : "Automatic — follows the shoot dates"
              }
              onChange={(v) =>
                set("liveScoringMode", v === "Always on" ? "on" : v === "Always off" ? "off" : "auto")
              }
              className="w-full sm:w-72"
            />
            <p className="mt-1 text-xs text-muted">
              On automatic it is enabled only while the shoot is running, and turns itself off when it finishes.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
